import { Component, Injector, signal, computed } from '@angular/core';
import { User } from '@common/models/user';
import { CommonService } from '@common/services/common.service';
import { QueryService } from '@common/services/query.service';
import { ToastrNotificationService } from '@common/services/toastr-notification.service';
import { DialogContentBase, DialogRef } from '@progress/kendo-angular-dialog';
import { validatePassword } from '@common/utils/password-validation';

@Component({
    selector: 'app-change-password-dialog',
    templateUrl: './change-password-dialog.component.html'
})
export class ChangePasswordDialogComponent extends DialogContentBase {
    oldPassword = '';
    newPassword = '';
    repeatNewPassword = '';

    isSubmitting = signal(false);

    private queryService: QueryService;
    private toastrNotificationService: ToastrNotificationService;
    private user: User;

    constructor(
        dialog: DialogRef,
        private injector: Injector,
        private commonService: CommonService,
        toastrNotificationService: ToastrNotificationService
    ) {
        super(dialog);
        this.queryService = commonService.queryService;
        this.toastrNotificationService = toastrNotificationService;
        this.user = commonService.user;
    }

    initialize(data: any, injector: Injector) {
        // This method can be empty since we're using constructor injection
    }

    get canConfirm(): boolean {
        const passwordStrengthValid = validatePassword(this.newPassword);

        return (
            !!this.oldPassword &&
            !!this.newPassword &&
            !!this.repeatNewPassword &&
            this.newPassword === this.repeatNewPassword &&
            passwordStrengthValid &&
            !this.isSubmitting()
        );
    }

    get passwordsMatch(): boolean {
        if (!this.newPassword || !this.repeatNewPassword) return true;
        return this.newPassword === this.repeatNewPassword;
    }

    cancel() {
        this.dialog.close(null);
    }

    async confirm() {
        if (!this.canConfirm) return;

        this.isSubmitting.set(true);

        try {
            await this.queryService.getCommandMutation().mutateAsync({
                command: 'ChangePassword',
                data: {
                    userName: this.user.userName,
                    oldPassword: this.oldPassword,
                    newPassword: this.newPassword,
                    repeatNewPassword: this.repeatNewPassword
                }
            });

            this.toastrNotificationService.show({
                type: 'success',
                message: 'Password changed successfully'
            });

            this.dialog.close(true);
        } catch (error) {
            // Don't show custom error message - let the system handle it
            // The system already shows a proper error modal with the server message
        } finally {
            this.isSubmitting.set(false);
        }
    }
}
